import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, History } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import { useExtractionHistory } from '../hooks/useExtractionHistory';
import ExtractionHistoryFilters from './ExtractionHistoryFilters';
import ExtractionHistoryTable from './ExtractionHistoryTable';
import { createComponentLogger } from '@/utils/logger';
import { useToast } from '@/hooks/use-toast';
import { getAttachmentUrl } from '@/services/encompassDocumentService';

const log = createComponentLogger('DocumentExtractionHistory', 'wiseboard');

interface Props {
  organizationId?: string;
  defaultCollapsed?: boolean;
}

export function DocumentExtractionHistory({ organizationId, defaultCollapsed = true }: Props) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const { toast } = useToast();

  const {
    items,
    totalCount,
    isLoading,
    error,
    filters,
    page,
    pageSize,
    totalPages,
    lastRefreshedAt,
    setSearch,
    setStatus,
    setGpu,
    setDatePreset,
    setPage,
    refresh,
    clearFilters
  } = useExtractionHistory({ organizationId, enabled: !isCollapsed });

  const handleRetry = async (id: string) => {
    log.info('Retry requested for document extraction', { id });
    toast({
      title: 'Coming Soon',
      description: 'Retry functionality will be available in a future update.'
    });
  };

  const handleViewFile = async (storagePath: string) => {
    log.info('Viewing file', { storagePath });
    try {
      const signedUrl = await getAttachmentUrl(storagePath);
      if (signedUrl) {
        window.open(signedUrl, '_blank');
      } else {
        toast({
          title: 'Error',
          description: 'Could not generate file URL',
          variant: 'destructive'
        });
      }
    } catch (error) {
      log.error('Failed to get file URL', { storagePath }, error);
      toast({
        title: 'Error',
        description: 'Failed to access file',
        variant: 'destructive'
      });
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader
        className={cn(
          'cursor-pointer hover:bg-muted/50 transition-colors flex flex-row items-center justify-between py-4',
          isCollapsed && 'pb-4'
        )}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center gap-2">
          <History className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-lg font-semibold">
            Document Extraction History
          </CardTitle>
          {!isCollapsed && totalCount > 0 && (
            <span className="text-sm text-muted-foreground">
              ({totalCount.toLocaleString()} documents)
            </span>
          )}
        </div>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
          {isCollapsed ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronUp className="h-4 w-4" />
          )}
        </Button>
      </CardHeader>

      {!isCollapsed && (
        <CardContent className="space-y-4 pt-0">
          {error ? (
            <div className="text-center py-8 text-red-500">
              <p className="font-medium">Failed to load extraction history</p>
              <p className="text-sm mt-1">{error}</p>
              <Button variant="outline" size="sm" onClick={refresh} className="mt-4">
                Try Again
              </Button>
            </div>
          ) : (
            <>
              <ExtractionHistoryFilters
                filters={filters}
                totalCount={totalCount}
                page={page}
                pageSize={pageSize}
                isLoading={isLoading}
                onSearchChange={setSearch}
                onStatusChange={setStatus}
                onGpuChange={setGpu}
                onDatePresetChange={setDatePreset}
                onRefresh={refresh}
                onClearFilters={clearFilters}
              />
              {lastRefreshedAt && (
                <p className="text-xs text-muted-foreground -mt-2">
                  Last refreshed {formatDistanceToNow(lastRefreshedAt, { addSuffix: true })}
                </p>
              )}

              <ExtractionHistoryTable
                items={items}
                isLoading={isLoading}
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                onRetry={handleRetry}
                onViewFile={handleViewFile}
              />
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export default DocumentExtractionHistory;
